import { useEffect, useState, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ApiError } from '../api/client'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'

export function Button({
  variant = 'primary',
  busy = false,
  block = false,
  className = '',
  disabled,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; busy?: boolean; block?: boolean }) {
  const classes = ['btn', `btn-${variant}`, block ? 'btn-block' : '', className].filter(Boolean).join(' ')
  return (
    <button type="button" {...rest} disabled={disabled || busy} aria-busy={busy || undefined} className={classes}>
      {busy && <span className="spinner spinner-sm" aria-hidden="true" />}
      {children}
    </button>
  )
}

export function Card({ title, actions, children, className = '' }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <header className="card-head">
          {title && <h2>{title}</h2>}
          {actions && <div className="row">{actions}</div>}
        </header>
      )}
      <div className="card-body">{children}</div>
    </section>
  )
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="page-header">
      <div>
        <h1>{title}</h1>
        {subtitle && <p className="subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="row">{actions}</div>}
    </div>
  )
}

export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  )
}

export type Tone = 'neutral' | 'ok' | 'warn' | 'danger' | 'accent'

export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`badge badge-${tone}`}>{children}</span>
}

export function ProgressBar({ value, tone = 'accent', label }: { value: number; tone?: Tone; label?: string }) {
  const clamped = Math.max(0, Math.min(100, value))
  return (
    <div className={`progress progress-${tone}`} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(clamped)} aria-label={label}>
      <div className="progress-fill" style={{ width: `${clamped}%` }} />
    </div>
  )
}

export function LoadingBlock({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="loading" role="status">
      <span className="spinner" aria-hidden="true" />
      {label}
    </div>
  )
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) return error.message
  return 'Something went wrong.'
}

/** An error; one about credits or the plan (402) links to Billing. */
export function ErrorNote({ error, message }: { error?: unknown; message?: string }) {
  const billing = error instanceof ApiError && error.status === 402
  return (
    <div className="error-note" role="alert">
      {message ?? errorMessage(error)}
      {billing && (
        <>
          {' '}
          <Link to="/studio/billing">Open Billing</Link>
        </>
      )}
    </div>
  )
}

export function Stat({ label, value, note }: { label: string; value: ReactNode; note?: ReactNode }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {note && <div className="stat-note">{note}</div>}
    </div>
  )
}

export function EmptyState({ title, body, action }: { title: string; body?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <p className="empty-title">{title}</p>
      {body && <p className="muted">{body}</p>}
      {action}
    </div>
  )
}

export function CodeBlock({ value }: { value: unknown }) {
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2)
  return <pre className="code">{text}</pre>
}

export function Logo({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true" className="logo">
      <rect width="32" height="32" rx="8" fill="var(--accent)" />
      <path d="M16 5.5 18.6 13.4 26.5 16 18.6 18.6 16 26.5 13.4 18.6 5.5 16 13.4 13.4Z" fill="var(--accent-ink)" />
    </svg>
  )
}

/** A small link-style button that copies `text` and says so. */
export function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false)
  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 1500)
    return () => clearTimeout(timer)
  }, [copied])
  return (
    <button
      type="button"
      className="tx-link small"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => setCopied(true))
      }}
    >
      {copied ? 'Copied' : label}
    </button>
  )
}

/** A secret shown once (an API key, a signing secret), with Copy and Done. */
export function SecretNotice({ secret, message, onDone }: { secret: string; message: string; onDone: () => void }) {
  const [copied, setCopied] = useState(false)
  useEffect(() => setCopied(false), [secret])
  return (
    <div className="notice stack-sm">
      <strong>{message}</strong>
      <div className="row">
        <code className="secret">{secret}</code>
        <Button
          variant="secondary"
          onClick={() => {
            void navigator.clipboard?.writeText(secret).then(() => setCopied(true))
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button variant="ghost" onClick={onDone}>Done</Button>
      </div>
    </div>
  )
}

/** A read-only value to copy: a feed address, an embed code. */
export function CopyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="copy-field">
      <span className="field-label">{label}</span>
      <div className="row">
        <input readOnly value={value} aria-label={label} onFocus={(event) => event.currentTarget.select()} />
        <CopyButton text={value} />
      </div>
    </div>
  )
}
